import React from 'react';
import { theme } from 'antd';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AvailabilityCalendar, { IBookingRange } from './AvailabilityCalendar';
import { BookingRentalMode } from '../interfaces/heavyDutyMachinery/IBooking';
import { CORAL } from '../theme/creamyTokens';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

// Same three options as desktop's own chips (renderWhenField in
// HomeSearchBar) -- 'custom' excluded, since this screen already has a full
// calendar/timeline for a manual pick, unlike desktop's single bar (where
// it's the hidden-DatePicker 4th chip instead).
const QUICK_AVAILABILITY_OPTIONS: { value: 'now' | '+2h' | '+3h'; labelKey: string }[] = [
  { value: 'now', labelKey: TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_NOW },
  { value: '+2h', labelKey: TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_PLUS_2H },
  { value: '+3h', labelKey: TRANSLATIONS.HOMEPAGE_SEARCH_AVAILABILITY_PLUS_3H },
];

// There's no specific host's own MaxAdvanceBookingDays/MinDurationMinutes to
// read yet (see AvailabilityCalendar's optional spaceId) -- generic enough
// to browse freely rather than reflect any real space.
const SEARCH_CALENDAR_MAX_ADVANCE_DAYS = 365;
const SEARCH_CALENDAR_MIN_DURATION_MINUTES = 60;
// Taller than the embedded booking-widget default (56px, see
// HourlyRangeTimeline) -- this screen has nothing else competing for space,
// so dragging the hour handles gets noticeably more comfortable/precise.
const SEARCH_TIMELINE_HEIGHT = 64;

interface HomeSearchWhenScreenProps {
  rentalMode: string | undefined;
  onSelectionChange: (range: IBookingRange | undefined) => void;
  /** Which quick-availability chip (below), if any, is genuinely active --
   * undefined means none of them, e.g. before the guest has touched
   * anything (unlike desktop, which defaults its own chips to "Ahora" since
   * its submit always resolves one regardless; this screen's chips are a
   * separate explicit action, so nothing shows selected until one actually
   * is), or after a manual calendar pick. Derived by HomeSearchBar, not the
   * same thing as its own shared `preset` state -- see that derivation's
   * own comment. */
  activePreset: 'now' | '+2h' | '+3h' | undefined;
  onQuickAvailability: (preset: 'now' | '+2h' | '+3h') => void;
  onConfirm: () => void;
}

/**
 * The mobile search overlay's "When" subscreen -- the real
 * AvailabilityCalendar (see that component's own spaceId comment) full
 * width, with HourlyRangeTimeline (built into AvailabilityCalendar, not
 * duplicated here) appearing below once a day is picked in Hourly mode.
 * Overnight gets the calendar's own day-range picker, DayPass its
 * single-day picker -- neither shows a timeline. rentalMode drives which of
 * the three since it's chosen first now (see HomeSearchBar's own reordered
 * main screen); unset falls back to Overnight, matching the prior
 * accordion's own fallback.
 *
 * Hourly mode additionally gets the same Now/+2h/+3h quick-availability
 * chips desktop's own search bar already has (see QUICK_AVAILABILITY_OPTIONS
 * above), right above the calendar -- tapping one resolves immediately
 * (same as picking a specific slot below does), without needing to scroll
 * to and pick an exact day/time when "as soon as possible" is really what's
 * wanted. Genuinely picking a day/time in the calendar instead still works
 * exactly as before, and un-highlights whichever chip was active.
 */
const HomeSearchWhenScreen: React.FC<HomeSearchWhenScreenProps> = ({
  rentalMode,
  onSelectionChange,
  activePreset,
  onQuickAvailability,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const mode: BookingRentalMode = rentalMode === 'Hourly' || rentalMode === 'DayPass' ? rentalMode : 'Overnight';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 20px' }}>
        {mode === 'Hourly' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {QUICK_AVAILABILITY_OPTIONS.map((opt) => {
              const selected = activePreset === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onQuickAvailability(opt.value)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 999,
                    border: `1.5px solid ${selected ? CORAL : token.colorBorderSecondary}`,
                    background: selected ? CORAL : token.colorBgContainer,
                    color: selected ? '#fff' : token.colorText,
                    fontWeight: selected ? 600 : 400,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  {translate(t, opt.labelKey)}
                </button>
              );
            })}
          </div>
        )}
        <AvailabilityCalendar
          key={mode}
          mode={mode}
          maxAdvanceBookingDays={SEARCH_CALENDAR_MAX_ADVANCE_DAYS}
          minDurationMinutes={SEARCH_CALENDAR_MIN_DURATION_MINUTES}
          bufferMinutes={0}
          timelineHeight={SEARCH_TIMELINE_HEIGHT}
          onSelectionChange={onSelectionChange}
        />
      </div>

      <div
        style={{
          padding: '16px 20px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          flexShrink: 0,
        }}
      >
        <motion.button
          type="button"
          onClick={onConfirm}
          whileTap={{ scale: 0.98 }}
          style={{
            width: '100%',
            padding: '15px 26px',
            borderRadius: 999,
            border: 'none',
            background: CORAL,
            color: '#fff',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_WHEN_CONFIRM_BUTTON)}
        </motion.button>
      </div>
    </div>
  );
};

export default HomeSearchWhenScreen;
